import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import { createEmptyTelemetry } from "../../../../src/lib/audio";
import { useStoreSelector } from "../../../../src/lib/store";
import { getLabActions } from "../labActions";
import { setMasterMeterHost } from "../lib/masterMeter";
import { setTransportRecordMeterHost } from "../lib/transportRecordMeter";
import { transportStore } from "../store";
import { uiStore } from "../store";
import logoUrl from "../../logo.svg";
import { ToolbarMenus } from "./ToolbarMenus";
import { tbGroup, tbSep } from "./toolbarChrome";

function TbIcon({
  children,
  className = "",
  active,
  primary,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  active?: boolean;
  primary?: boolean;
}) {
  // Class hooks (not Tailwind tones): unlayered `.tb-icon.active` + `button.primary`
  // in lab.css so disabled+active still wins (R6.3). Form defaults stay `@layer base`.
  const classes = ["tb-icon", primary ? "primary" : "", active ? "active" : "", className]
    .filter(Boolean)
    .join(" ");
  return (
    <button type="button" className={classes} {...props}>
      {children}
    </button>
  );
}

function TbSvg({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} aria-hidden={true}>
      {children}
    </svg>
  );
}

function TransportButtons() {
  const armed = useStoreSelector(transportStore, s => s.armed);
  const audioStarted = useStoreSelector(transportStore, s => s.audioStarted);
  const playing = useStoreSelector(transportStore, s => s.playing);
  const loop = useStoreSelector(transportStore, s => s.loop);
  const follow = useStoreSelector(transportStore, s => s.followPlayhead);

  return (
    <div className={tbGroup}>
      <TbIcon
        primary
        active={audioStarted}
        title={audioStarted ? "Restart audio" : "Enable audio"}
        aria-label={audioStarted ? "Restart audio" : "Enable audio"}
        onClick={() => void getLabActions()?.enableAudio()}
      >
        <TbSvg>
          <path d="M12 2v10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M6.3 6.3a8 8 0 1 0 11.4 0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </TbSvg>
      </TbIcon>
      <div className={tbSep} role="separator" />
      <TbIcon
        title="Play selected patterns"
        aria-label="Play selected patterns"
        disabled={!armed}
        active={playing}
        onClick={() => getLabActions()?.play()}
      >
        <TbSvg>
          <path d="M8 5.14v13.72L19 12 8 5.14z" fill="currentColor" />
        </TbSvg>
      </TbIcon>
      <RecordButton />
      <TbIcon
        title="Stop"
        aria-label="Stop"
        disabled={!armed}
        onClick={() => getLabActions()?.stop()}
      >
        <TbSvg>
          <rect x="6" y="6" width="12" height="12" rx="1.5" fill="currentColor" />
        </TbSvg>
      </TbIcon>
      <div className={tbSep} role="separator" />
      <TbIcon
        title="Follow playhead"
        aria-label="Follow playhead"
        aria-pressed={follow}
        active={follow}
        onClick={() => getLabActions()?.toggleFollowPlayhead()}
      >
        <TbSvg>
          <path d="M12 3v18M4 12h4M16 12h4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
        </TbSvg>
      </TbIcon>
      <TbIcon
        title={loop ? "Loop on" : "Loop off"}
        aria-label="Loop pattern"
        aria-pressed={loop}
        active={loop}
        onClick={() => getLabActions()?.toggleLoop()}
      >
        <TbSvg>
          <path
            d="M17 2.5 20.5 6 17 9.5M6.5 6H19M7 21.5 3.5 18 7 14.5M17.5 18H5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </TbSvg>
      </TbIcon>
    </div>
  );
}

function RecordButton() {
  const micRecording = useStoreSelector(transportStore, s => s.micRecording);
  const authorMicRecording = useStoreSelector(transportStore, s => s.authorMicRecording);
  const fillRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const fill = fillRef.current;
    if (!micRecording || !fill) {
      setTransportRecordMeterHost(null);
      return;
    }
    setTransportRecordMeterHost({
      fill,
      getLevel: () => getLabActions()?.getMicRecordLevel?.() ?? 0,
    });
    return () => setTransportRecordMeterHost(null);
  }, [micRecording]);

  return (
    <TbIcon
      className={micRecording ? "record" : ""}
      title={
        micRecording
          ? "Stop recording → new sampler track"
          : authorMicRecording
          ? "Stop Sampler Author recording first"
          : "Record mic over arrangement → new sampler track (enables audio)"
      }
      aria-label="Record to sampler"
      aria-pressed={micRecording}
      active={micRecording}
      disabled={authorMicRecording}
      onClick={() => void getLabActions()?.toggleMicRecord()}
    >
      {micRecording && (
        <span className="tb-record-meter" aria-hidden={true}>
          <span ref={fillRef} className="tb-record-meter-fill" />
        </span>
      )}
      <TbSvg>
        <circle cx="12" cy="12" r="6" fill="currentColor" />
      </TbSvg>
    </TbIcon>
  );
}

function PanelButtons() {
  const instrumentOpen = useStoreSelector(uiStore, s => s.instrumentSlot !== null);
  const patternsOpen = useStoreSelector(uiStore, s => s.patternsOpen);
  const samplesOpen = useStoreSelector(uiStore, s => s.samplesOpen);
  const fxOpen = useStoreSelector(uiStore, s => s.fxOpen);
  const mixerOpen = useStoreSelector(uiStore, s => s.mixerOpen);
  const performanceOpen = useStoreSelector(uiStore, s => s.performanceOpen);

  return (
    <div className={tbGroup}>
      <TbIcon
        title="Instruments (⌘/Ctrl+Shift+I)"
        aria-label="Instruments"
        aria-pressed={instrumentOpen}
        active={instrumentOpen}
        onClick={() => getLabActions()?.toggleInstrument()}
      >
        <TbSvg>
          <rect x="3" y="7" width="18" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M7 19V9M10 19v-4M13 19V11M17 19V9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </TbSvg>
      </TbIcon>
      <TbIcon
        title="FX (⌘/Ctrl+Shift+F)"
        aria-label="FX"
        aria-pressed={fxOpen}
        active={fxOpen}
        onClick={() => getLabActions()?.toggleFx()}
      >
        <TbSvg>
          <path d="M4 14v4M4 6v4M12 16v2M12 6v6M20 12v6M20 6v2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <circle cx="4" cy="12" r="2" fill="currentColor" />
          <circle cx="12" cy="14" r="2" fill="currentColor" />
          <circle cx="20" cy="10" r="2" fill="currentColor" />
        </TbSvg>
      </TbIcon>
      <TbIcon
        title="Arrange (⌘/Ctrl+Shift+A)"
        aria-label="Arrange"
        aria-pressed={patternsOpen}
        active={patternsOpen}
        onClick={() => getLabActions()?.togglePatterns()}
      >
        <TbSvg>
          <path d="M5 5h6v6H5zM13 5h6v6h-6zM5 13h6v6H5zM13 13h6v6h-6z" fill="none" stroke="currentColor" strokeWidth="2" />
        </TbSvg>
      </TbIcon>
      <TbIcon
        title="Samples (⌘/Ctrl+Shift+L)"
        aria-label="Samples"
        aria-pressed={samplesOpen}
        active={samplesOpen}
        onClick={() => getLabActions()?.toggleSamples()}
      >
        <TbSvg>
          <path
            d="M4 14v2M8 10v6M12 7v9M16 11v5M20 9v7"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </TbSvg>
      </TbIcon>
      <TbIcon
        title="Mixer (⌘/Ctrl+Shift+M)"
        aria-label="Mixer"
        aria-pressed={mixerOpen}
        active={mixerOpen}
        onClick={() => getLabActions()?.toggleMixer()}
      >
        <TbSvg>
          <path
            d="M6 4v16M12 4v16M18 4v16M4 9h4M10 15h4M16 7h4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </TbSvg>
      </TbIcon>
      <TbIcon
        title="Performance (⌘/Ctrl+Shift+P)"
        aria-label="Performance"
        aria-pressed={performanceOpen}
        active={performanceOpen}
        onClick={() => getLabActions()?.togglePerformance()}
      >
        <TbSvg>
          <path
            d="M4 5h4v14H4zM10 5h4v6h-4zM16 5h4v10h-4zM10 13h4v6h-4z"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          />
        </TbSvg>
      </TbIcon>
    </div>
  );
}

function NumberField({
  label,
  title,
  value,
  min,
  max,
  disabled,
  inputClassName,
  onCommit,
}: {
  label: string;
  title: string;
  value: number;
  min: number;
  max: number;
  disabled: boolean;
  inputClassName?: string;
  onCommit: (next: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  return (
    <label
      className="m-0 flex items-center gap-[0.3rem] text-[11px] tracking-[0.04em] text-lab-muted uppercase"
      title={title}
    >
      <span>{label}</span>
      <input
        type="number"
        min={min}
        max={max}
        disabled={disabled}
        value={draft}
        className={`rounded border border-lab-line bg-lab-bg px-[0.35rem] py-[0.28rem] text-[12px] text-lab-text disabled:opacity-40 ${inputClassName ?? "!w-[3.6rem]"}`}
        onChange={event => setDraft(event.target.value)}
        onBlur={() => {
          const next = Number(draft);
          if (Number.isFinite(next)) {
            onCommit(next);
          } else {
            setDraft(String(value));
          }
        }}
        onKeyDown={event => {
          if (event.key === "Enter") {
            (event.target as HTMLInputElement).blur();
          }
        }}
      />
    </label>
  );
}

function TransportFields() {
  const armed = useStoreSelector(transportStore, s => s.armed);
  const bpm = useStoreSelector(transportStore, s => s.bpm);
  const grid = useStoreSelector(transportStore, s => s.stepDivision);
  const len = useStoreSelector(transportStore, s => s.patternSteps);

  return (
    <div className={`${tbGroup} gap-[0.55rem]`}>
      <NumberField
        label="BPM"
        title="Tempo"
        value={bpm}
        min={20}
        max={400}
        disabled={!armed}
        onCommit={next => getLabActions()?.setBpm(next)}
      />
      <NumberField
        label="Grid"
        title="Step division — rows per whole note (@grid). 16 = 16th notes"
        value={grid}
        min={1}
        max={64}
        disabled={!armed}
        inputClassName="!w-[3.2rem] text-center"
        onCommit={next => getLabActions()?.setGrid(next)}
      />
      <NumberField
        label="Len"
        title="Pattern length"
        value={len}
        min={1}
        max={8192}
        disabled={!armed}
        inputClassName="!w-[4.4rem] text-center"
        onCommit={next => getLabActions()?.setPatternLength(next)}
      />
    </div>
  );
}

function TransportTime() {
  const current = useStoreSelector(transportStore, s => s.timeCurrent);
  const pattern = useStoreSelector(transportStore, s => s.timePattern);
  const composition = useStoreSelector(transportStore, s => s.timeComposition);
  const title = useStoreSelector(transportStore, s => s.timeTitle);
  const leftMeterRef = useRef<HTMLSpanElement>(null);
  const rightMeterRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const left = leftMeterRef.current;
    const right = rightMeterRef.current;
    if (!left || !right) {
      return;
    }
    setMasterMeterHost({
      left,
      right,
      getTelemetry: () => getLabActions()?.getTelemetry() ?? createEmptyTelemetry(),
    });
    return () => setMasterMeterHost(null);
  }, []);

  return (
    <div className={tbGroup}>
      <div
        className="relative inline-flex select-none items-baseline gap-[0.35rem] overflow-hidden rounded border border-lab-line bg-lab-bg px-[0.55rem] py-[0.28rem] font-lab-mono text-[12px] leading-[1.2] tracking-[0.02em] text-lab-text tabular-nums"
        title={title}
        aria-live="polite"
      >
        <span
          className="pointer-events-none absolute inset-0 z-0"
          aria-hidden={true}
        >
          <span
            ref={leftMeterRef}
            className="absolute top-0 right-0 left-0 h-[2px] origin-left bg-[#7dd3fc]/45 will-change-transform"
            style={{ transform: "scaleX(0)" }}
          />
          <span
            ref={rightMeterRef}
            className="absolute right-0 bottom-0 left-0 h-[2px] origin-left bg-[#7dd3fc]/40 will-change-transform"
            style={{ transform: "scaleX(0)" }}
          />
        </span>
        <span className="relative z-[1] min-w-[5ch] text-right text-slate-200">{current}</span>
        <span className="relative z-[1] text-lab-muted opacity-70" aria-hidden={true}>/</span>
        <span className="relative z-[1] min-w-[5ch] text-left text-lab-muted">{pattern}</span>
        <span className="relative z-[1] text-lab-muted opacity-70" aria-hidden={true}>/</span>
        <span className="relative z-[1] min-w-[5ch] text-left text-lab-muted opacity-85">{composition}</span>
      </div>
    </div>
  );
}

const ribbonGroove =
  "block h-2 rounded-tl-[10px] shadow-[inset_0_2px_3px_rgba(0,0,0,0.55),inset_0_1px_1px_rgba(0,0,0,0.45),inset_0_-1px_0_rgba(255,255,255,0.07),0_1px_0_rgba(255,255,255,0.05)]";

export function TransportToolbar() {
  return (
    <header
      className="toolbar relative z-[3] mx-[0.85rem] mt-[0.65rem] flex shrink-0 flex-wrap items-center gap-x-[0.55rem] gap-y-[0.45rem] rounded-lg border border-lab-line bg-lab-panel py-[0.4rem] pr-[200px] pl-[0.5rem] shadow-[0_6px_18px_rgba(0,0,0,0.22)] max-[900px]:pt-10 max-[900px]:pr-2"
      role="toolbar"
      aria-label="Transport"
    >
      <div
        className="pointer-events-none absolute top-[3px] right-0 z-0 flex flex-col items-end gap-[3px] overflow-hidden rounded-tr-[7px] pt-[0.22rem]"
        aria-hidden={true}
      >
        <span
          className={`${ribbonGroove} w-[19rem] bg-[linear-gradient(90deg,color-mix(in_srgb,#9fd0ff_52%,#3c4048)_0%,color-mix(in_srgb,#9fd0ff_24%,#25272b)_55%,color-mix(in_srgb,#9fd0ff_8%,var(--ribbon-tail))_100%)]`}
        />
        <span
          className={`${ribbonGroove} w-[14.2rem] bg-[linear-gradient(90deg,color-mix(in_srgb,#b8e986_48%,#3d4038)_0%,color-mix(in_srgb,#b8e986_21%,#272820)_55%,color-mix(in_srgb,#b8e986_7%,var(--ribbon-tail))_100%)]`}
        />
        <span
          className={`${ribbonGroove} w-[9.4rem] bg-[linear-gradient(90deg,color-mix(in_srgb,#e6b35a_50%,#463d2e)_0%,color-mix(in_srgb,#e6b35a_23%,#2c2721)_55%,color-mix(in_srgb,#e6b35a_8%,var(--ribbon-tail))_100%)]`}
        />
        <img
          className="absolute top-[calc(0.22rem+12px)] right-[2.2rem] block h-[18px] w-auto -translate-y-1/2 opacity-[0.92] drop-shadow-[0_1px_0_rgba(0,0,0,0.35)]"
          src={logoUrl}
          alt=""
          width={110}
          height={34}
        />
      </div>
      <TransportButtons />
      <div className={tbSep} role="separator" />
      <PanelButtons />
      <div className={tbSep} role="separator" />
      <TransportFields />
      <div className={tbSep} role="separator" />
      <TransportTime />
      <div className={tbSep} role="separator" />
      <ToolbarMenus />
    </header>
  );
}
